define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"underscore",
	"csi",
	"csi/def",
	"csi/def/Heading",
	"csi/def/Relationship",
	"csi/def/Request",
	"csi/def/Rule",
	"csi/heading/_base",
	"csi/html",
	"csi/locale",
	"csi/reflection",
	"csi/resolver",
	"csi/types",
	"csi/util",
	"csi/utility/defUtility",
	"csi/xml"
], function(declare, lang, _, csi, def, Heading, Relationship, Request, Rule, csiHeading, html, locale, reflection, resolver, types, util, defUtility, xml) {

	lang.mixin(def, {
		// Field Controls
		FIELD_CHECKBOX: "Checkbox",
		FIELD_CHECKBOX_MAP: "CheckboxMap",
		FIELD_COMMENT: "Comment",
		FIELD_CUSTOM: "Custom",
		FIELD_FILE: "File",
		FIELD_FILTERING_SELECT: "FilteringSelect",
		FIELD_MULTI_SELECT: "MultiSelect",
		FIELD_NUMBER: "Number",
		FIELD_PASSWORD: "Password",
		FIELD_TEXT: "Text",
		FIELD_TEXTAREA: "Textarea",
		FIELD_TIME: "Time"
	});

	/**
	 * Renderers are not yet fully supported, so make sure to only enable for certain controls.
	 * @param {Heading} h
	 * @param {string} type
	 * @param {string} format
	 */
	function _getRenderer(h, type, format) {
		if (!type) return null;

		if (h?.classRenderer) {
			return csiHeading.getClassRenderer(h.classRenderer, type, format, h.classRendererParams);
		}

		return csiHeading.getRenderer(type, format, h?.classRendererParams);
	}

	function _getRendererForControl(/*Heading?*/h, /*String*/typeControl, /*String*/type, /*String*/format) {
		// summary: determine renderer to use based on control type and format
		if (format == types.FORMAT_TEXTAREA) {
			type = xml.ATTR_STRING;
		} else switch (typeControl) {
			case def.FIELD_CHECKBOX: type = xml.ATTR_BOOLEAN; break;
			case def.FIELD_CUSTOM: return null;
			case def.FIELD_NUMBER: type = xml.ATTR_DOUBLE; break;
			case def.FIELD_TEXT: type = xml.ATTR_STRING; break;
			case def.FIELD_TEXTAREA: type = xml.ATTR_STRING; format = types.FORMAT_TEXTAREA; break;
			case def.FIELD_TIME: type = xml.ATTR_TIME; break;
		}

		return _getRenderer(h, type, format);
	}

	const Field = declare(null, {
		/** @property {IAggregateStoreResponse} aggregateStoreResponse - aggregate store response if field uses an aggregate store */
		//aggregateStoreResponse: null,

		//m_control: null,
		//m_name: null,
		//m_rca: null,

		// Boolean: indicates if this is required field
		//m_bRequired: null,

		// Boolean: indicates if field can be edited in form; this does not reflect current disabled state of widget, but the possibility that it can be enabled at some point
		//bCanEdit: false,

		// Boolean: combine this field with previous field on same row
		//bCombineWithPrevious: false,

		// Boolean: indicates if the expression should NOT be evaluated on the client
		//bDisableExpressionEvaluation: false,

		// Boolean: used in rare cases to override editability of an attribute (such as for special handling of __Parent__ relationship)
		//bForceEditable: false,

		// Boolean: use renderer class to render widget rather than widget used by form (eventually this will be the default)
		//bUseRenderer: false,

		// Boolean: runtime flag added to field to indicate it should not be exposed (at least in normal way)
		//bHidden: false,

		// Boolean: display the label of the field when the bCombineWithPrevious equals true
		//bHideLabelForCombinedField : false

		// Heading: class heading definition for field id
		//heading: null,

		// string: card to use in popup table dialog rather than the default card
		//dialogCardId: null,

		// String: if specified, then send field as Parameter of action Request with given id
		//idParameter: null,

		// String|Object: widget class to use for form validation message under field (default is "csi/widget/ErrorText")
		//messageClass: null,

		/** @property {boolean} notApplicable - indicates field is not currently applicable to form processing */
		//notApplicable: false,

		/** @property {boolean} omitFromReport - indicates field should be omitted from form report because not an attribute on target class of form (can't always rely on m_id or m_rca) */
		//omitFromReport: false,

		// Renderer: renderer to use for field (if known)
		//ren: null,

		/** @property {boolean} startedWithDisabledContinuousExpression - indicates if existing attribute had its contiuous expression disabled before opening the form */
		startedWithDisabledContinuousExpression: false,

		// Boolean: indicates if field value was changed through form logic, rather than interactively by user
		//_bInternalChange: false,

		// Boolean: indicates if field has already been resolved
		//_bResolved: false,

		// Boolean: indicates if field value was changed explicitly
		//_bUpdated: false,

		// String: key for validation expression that will be sent as error code if invalid
		//_validationKey: null,

		// Any: indicates if field's value was set through logic/expression, in which case it should continue to be set
		// through logic until manually updated
		//_valueDefault: null,

		/** @property {string} _expressionResult - store the result of the expression */
		_expressionResult: null,

		constructor: function(obj) {
			// optional (similar to Heading)
			this.m_id = util.getProperty(obj, 'Id');
			this.m_type = util.getProperty(obj, 'Type', false);
			this.m_typeControl = util.getProperty(obj, 'Control');
			this.m_class = util.getProperty(obj, 'Class');
			this.m_dependency = util.getProperty(obj, 'Dependency');
			this.m_format = util.getProperty(obj, 'Format');
			this.m_onDraw = util.getProperty(obj, 'OnDraw');
			this.m_onChange = util.getProperty(obj, 'OnChange');
			this.m_onValidate = util.getProperty(obj, 'OnValidate');
			this.m_relationship = util.getDef(obj, 'Relationship', Relationship);	// ref attributes only
			this.m_request = defUtility.getDef(obj, 'Request', (element) => new Request(element));
			this.m_right = util.getProperty(obj, 'Right');
			this.m_rules = util.getDefs(obj, 'Rule', Rule);
			this.m_path = util.getProperty(obj, 'Path');
			this.idParameter = util.getProperty(obj, 'ParameterId');
			this.bPersist = util.getBooleanProperty(obj, 'Persist');
			this.m_bRequired = util.getBooleanProperty(obj, 'Required');
			this.m_bInitialize = util.getBooleanProperty(obj, 'Initialize');

			// initialize widget parameters
			var widgetParams = util.getProperty(obj, 'WidgetParams');
			if (widgetParams) {
				this.widgetParams = csi.fromJson(widgetParams);
			}

			this.widgetParams = this.widgetParams || {};
			var widgetPlaceholder = util.getProperty(obj, 'Placeholder');
			if (widgetPlaceholder) {
				this.widgetParams.placeholder = locale.resource(widgetPlaceholder);
			}

			// required
			this.m_label = util.getProperty(obj, 'Label');
		},

		resolve: function(/*LocaleResolver*/lr, /*Class*/rcRoot, /*Node*/nodeRoot) {
			this.resolveWithParameters(lr, rcRoot, nodeRoot, this.m_id);
		},

		resolveWithParameters: function(
				/*LocaleResolver*/lr,
				/*Class*/rcRoot,
				/*Node*/nodeRoot,
				/*String*/targetId,
				/*String*/targetKey,
				/*String*/attributeType
		) {
			// don't resolve again
			if (this._bResolved) {
				return;
			}

			var h;

			// change any path to "Child:CurrentRevision" to "Child:LatestRevision"; ideally all forms would be configured to use LatestRevision,
			// but there are a number where that is not the case. Rather than reconfigure everything and migrate existing CreateFormDefinition nodes,
			// just treat the two paths the same. The user currently has no control in the UI over which revision to use on the form, so force 
			// OOTB form fields to use LatestRevision
			if (this.m_path?.startsWith(reflection.PATH_CURRENT_REVISION)) {
				this.m_path = this.m_path.replace(reflection.PATH_CURRENT_REVISION, reflection.PATH_LATEST_REVISION);
			}

			// resolve field attribute type and assign format if not already assigned
			if (this.m_id) {
				rcRoot = resolver.resolveClass(rcRoot, nodeRoot, null, this.m_path);

				var rca = this.m_rca = reflection.getClassAttribute(rcRoot, targetId);
				if (rca) {
					if (rca.getOwner().isDerivedFrom(rcRoot)) {
						rcRoot = rca.getOwner();
					}

					if (this.m_format == null) {
						this.m_format = rca.getFormat();
					}

					if (this.m_bRequired == null) {
						this.m_bRequired = rca.isRequired;
					}
				}

				// resolve Node/Headings/Heading for attribute - first try on current class with path;
				// otherwise try on class after traversing path
				const classHeading = def.getClassHeading(rcRoot, targetId, this.m_path) || def.getClassHeading(rcRoot, targetId);
				if (classHeading) {
					h = new Heading(classHeading[0]);

					this.m_dependency = this.m_dependency || h.m_dependency;

					if (this.m_format == null) {
						this.m_format = h.m_format;
					}

					// inherit action rules for heading, but not styling rules
					if ((this.m_rules == null) && h.m_action) {
						this.m_rules = _.where(h.m_action.m_rules, {bApplyToForm: true});
					}

					// inherit request
					var ha = h.m_action;
					if (!this.m_request && ha && ha.m_request && !ha.isDerived()) {
						this.m_request = ha.m_request;
					}
				} else if (rca) {
					h = new Heading();
					h.m_id = targetId;
				}
			}

			// C8FB-1316: m_name is not  overriden by m_label whenever a form field is created
			this.m_name ||= this.m_label || this.m_id;
			this.m_label = lr.resolveHeadingNames(targetKey || this.getKey(), rcRoot ? rcRoot.getType() : null, targetId, null, this.m_label).name;

			// set type of control based on attribute type
			if (attributeType) {
				this.m_type = attributeType;
			} else {
				attributeType = this.getType();
			}

			if (this.m_typeControl) {
				this.ren = _getRendererForControl(h, this.m_typeControl, this.m_type, this.m_format);
			} else if (attributeType) {
				this.setType(attributeType, this.m_format, h);
			}

			// create heading for field for renderers to use even if not an attribute
			if (!h && this.m_type && this.m_name) {
				h = lang.mixin(new Heading(), {
					m_id: "__" + this.m_name
				});
			}

			// copy field properties to heading
			if (h) {
				h.m_bRequired = h.m_bRequired || this.m_bRequired;
				h.m_format = this.m_format;
				h.m_path = this.m_path;
				h._rca = this.m_rca;
				this.heading = h;
			}

			this._bResolved = true;
		},

		/**
		 * Set type of control based on attribute type.
		 * @param {string} type
		 * @param {string} format
		 * @param {Heading} [h]
		 */
		setType: function(type, format, h) {
			var typeControl;

			this.m_type = type;
			this.m_format = format;

			switch (type) {
				case xml.ATTR_BOOLEAN: typeControl = def.FIELD_CHECKBOX; break;
				case xml.ATTR_BOOLEANMAP: typeControl = def.FIELD_CHECKBOX_MAP; type = null; break;
				case xml.ATTR_TIME: typeControl = def.FIELD_TIME; break;
				case xml.ATTR_URL: typeControl = def.FIELD_FILE; type = null; break;

				case xml.ATTR_INTEGER: {
					var cons = this.widgetParams.constraints = this.widgetParams.constraints || {};
					cons.places = cons.places || 0;

					if (cons.max == null) {
						cons.max = 2000000000;
					}

					if (cons.min == null) {
						cons.min = -2000000000;
					}
					// fall through
				}

				case xml.ATTR_DOUBLE:
				case xml.ATTR_FLOAT: {
					typeControl = def.FIELD_NUMBER;
					this.bUseRenderer = true;
					break;
				}

				default: {
					var baseType = xml.attributeTypeGetBase(type) || type;
					if (baseType == xml.ATTR_ENUM || format == types.FORMAT_SELECTION) {
						// if it's an enum container, then control type is MultiSelect, if it's an enum, then it's FilteringSelect
						if (xml.attributeTypeIsContainer(type)) {
							typeControl = def.FIELD_MULTI_SELECT;
						} else {
							typeControl = def.FIELD_FILTERING_SELECT;
						}
						type = null;
					} else if (format == types.FORMAT_TEXTAREA) {
						typeControl = def.FIELD_TEXTAREA;
					} else if ((type == xml.ATTR_REF)) {
						typeControl = def.FIELD_FILTERING_SELECT;

						// by default form uses custom code to render widget, so reset type so renderer will not be used
						if (!this.bUseRenderer) {
							type = null;
						}
					} else if (type.indexOf(xml.ATTR_REF) == 0) {
						typeControl = def.FIELD_MULTI_SELECT;
						type = null;
					} else {
						typeControl = def.FIELD_TEXT;
						this.bUseRenderer = true;
					}
					break;
				}
			}

			this.m_typeControl = typeControl;
			this.ren = _getRenderer(h || this.heading, type, format);
		},

		getKey: function() {
			// summary: the name of the field must be unique
			return this.m_name;
		},

		getQueryPath: function() {
			// summary: return path needed to query information for field
			var p = this.m_path;

			if (this.m_rca && xml.attributeTypeIsRef(this.m_rca.getType())) {
				p = xml.pathAddStep(this.m_path, xml.PATH_OP_CHILD, this.m_id);
			}

			return p;
		},

		hasExpression: function() {
			// summary: return true if field's attribute has expression
			return this.m_rca && this.m_rca.getExpression();
		},

		/**
		 * Return true if field is default value - either it has not been changed, or it was changed
		 * to a default value through logic/expression
		 * @param {any} [defaultValue]
		 * @return {boolean}
		 */
		isDefaultValue: function(defaultValue) {
			if (defaultValue === undefined) {
				if (!this._bUpdated) {
					return true;
				}

				defaultValue = this._valueDefault;
			}

			var value = this.getValue();
			if (value == defaultValue) {
				return true;
			}

			// sometimes strings differ by whitespaces only - we consider them equal
			return _.isString(value) && _.isString(defaultValue) && (value.trim() === defaultValue.trim());
		},

		/**
		 * Determine if field is required in terms of widget attached
		 * Note: the Field itself could be required even though this function reture false
		 */
		isControlRequired: function() {
			const control = this.m_control;
			console.assert(!!control, "Control for this Field - " + this.getControlName() + " is not initialized yet");
			return control.required;
		},

		isUpdated: function() {
			// summary: determine if field value has changed
			return this._bUpdated;
		},

		focusField: function() {
			var w = this.m_control;

			if (w && !html.isDisabled(w)) {
				w.focus();

				if (w.select) {
					w.select();
				} else if (w.focusNode && w.focusNode.select) {
					//Highlight text value of the focused field.
					w.focusNode.select();
				}
			}
		},

		getDomNode: function() {
			var dom = this.m_control;
			if (dom && html.isWidget(dom)) {
				return dom.domNode;
			}

			return dom;
		},

		getFocusNode: function() {
			var dom = this.m_control;
			if (dom && html.isWidget(dom)) {
				return dom.focusNode;
			}

			return dom;
		},

		getEnumId: function(/*String?*/value) {
			return (this.getType() == xml.ATTR_ENUM) ? xml.enumGetId(value || this.m_rca.getValue()) : null;
		},

		getType: function() {
			var t = this.m_type;

			if (this.m_type === false) {
				if (this.m_rca) {
					t = this.m_rca.getType();
					if (t == xml.ATTR_REF && this.m_typeControl == def.FIELD_MULTI_SELECT) {
						t = xml.ATTR_REFLIST;
					}
				} else if (this.m_format == types.FORMAT_SELECTION) {
					t = xml.ATTR_STRING;
				} else switch (this.m_typeControl) {
					case def.FIELD_CHECKBOX: t = xml.ATTR_BOOLEAN; break;
					case def.FIELD_CHECKBOX_MAP: t = xml.ATTR_BOOLEANMAP; break;
					case def.FIELD_FILE: t = xml.ATTR_URL; break;
					case def.FIELD_FILTERING_SELECT: t = xml.ATTR_REF; break;
					case def.FIELD_MULTI_SELECT: t = xml.ATTR_REFLIST; break;
					case def.FIELD_TIME: t = xml.ATTR_TIME; break;
				}

				if (!t && (this.m_id || this.m_label)) {
					t = xml.ATTR_STRING;
				}

				this.m_type = t || null;
			}

			return this.m_type;
		},

		/**
		 * Return reflection class for field
		 * @return {C8NodeType}
		 */
		getOwnerType() {
			const rc = this.heading?._rc || this.m_rca?.getOwner();
			return rc?.type;
		},

		/**
		 * Returns a name for the field
		 * @return {String} name
		 */
		getControlName: function() {
			var controlName = (this.m_name == null) ? (this.m_label || this.m_id) : this.m_name;
			return this.combinedFormType ? (this.combinedFormType + "/" + controlName) : controlName;
		},

		/**
		 * Returns a control name taking into account the path
		 * @return {String} name
		 */
		getUniqueControlName() {
			return this.m_path ? this.m_path + "/" + this.getControlName() : this.getControlName();
		},

		/** return the formatted value  */
		getFormattedValue() {
			const type = this.getType();
			const value = this.getValue();
			let formattedValue = value;

			if (type === xml.ATTR_ENUM) {
				formattedValue = locale.getEnumName(value);
			}

			return formattedValue;
		},

		/**
		 * Get name for given value from field store
		 * @param {cnl} value
		 * @return {String} name
		 */
		getNameForValue(value) {
			const fieldStore = this.getStore();
			return fieldStore?.get(value)[fieldStore.labelProperty];
		},

		/**
		 * Return data store for control
		 * @return {Store<object, object, object>}
		 */
		getStore() {
			const {m_control} = this;
			return html.isWidget(m_control) ? m_control.get("store") : null;
		},

		/**
		 * Get the string url or an array of url from the field and return the display name for these values. 
		 * @return {String}
		 */
		getStringValue() {
			const value = this.getValue();
			let displayValueOfField;

			// check if value is a string and valid url, return the name of the value
			if (typeof value === "string" && xml.isValidURL(value)) {
				displayValueOfField = this.getNameForValue(value);
			}

			// if the value is a string but not a valid url or if value is an array of nodes, use the other two methods
			return displayValueOfField || this.getFocusNode().value || this.getDomNode().textContent;
		},

		getValue: function() {
			var w = this.m_control;
			var value;

			if (w) {
				if (this.ren) {
					// FIX - assumption right now is that ctx is not needed to extract value and
					// heading parameter can be substituted with field since it only needs to support m_format property
					// For number fields, we already do the parsing in the number text box, so don't need to do it again.
					value = this.ren.fromHtmlDynamic(null, this, w, true);
				} else if (w.model && w.model.store) {
					// FIX: need to make this consistent with other widgets
					var store = w.model.store;

					store.fetchItemByIdentity({
						identity: this.m_id,
						onItem: function(item) {
							value = {};

							if (item) {
								_.each(store.getValues(item, "children"), function(c) {
									value[store.getIdentity(c)] = store.getValue(c, "value");
								});
							}
						}
					});
				} else if (html.isWidget(w)) {
					// in dijit/form/FilteringSelect, the value(valueNode.value) won't be set until the store is available
					// however, the property value is there, we would use this property directly, it happens for async store
					value = w.get("externalValue") || w.get("value") || w.value;
				} else if (w.value != null) {
					// extract value from native browser control
					value = String(w.value).trim();
					if (value) {
						// handle numbers correctly
						if (xml.attributeTypeIsNumeric(this.getType())) {
							value = types.getInternalNumber(value);

							// if editing a percentage, then need to divide by 100
							var percentValue = types.getPercentage(value, this.m_format);
							if (percentValue != null) {
								value = percentValue;
							}
						}
					}
				}
			}

			return value;
		}
	});

	Field.getRendererForControl = _getRendererForControl;
	return Field;
});

